import classNames from 'classnames';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { AsyncLoadStatus } from '../../common/types';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import * as Icon from '../../components/icons';
import { InputSearch } from '../../components/inputs/InputSearch';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import {
  userSettings,
  userSettingsLoadStatus,
} from '../../store/warehouse.state';
import { UserExtraSettings } from '../../store/warehouse.types';
import useUpdateUserSettings from '../../warehouse/hooks/useUpdateUserSettings';
import { EVENT_STATS_CONFIG } from '../constants/actuality.event.config';
import { actualityVisibleDeviceIndicatorsSelector } from '../store/actuality.state';

// Device indicator configuration - subset of EVENT_STATS_CONFIG for device-related indicators
const DEVICE_INDICATORS_CONFIG = EVENT_STATS_CONFIG.filter(
  stat => stat.key === 'agentEnergy' || stat.key === 'agentSignalStrength',
);

const DeviceStatusIndicatorsManagement = () => {
  const { t } = useTranslation('feed');
  const callUpdate = useUpdateUserSettings();
  const userSettingsData = useRecoilValue(userSettings);
  const updateStatus = useRecoilValue(userSettingsLoadStatus);
  const visibleDeviceIndicators = useRecoilValue(
    actualityVisibleDeviceIndicatorsSelector,
  );
  const [searchTerm, setSearchTerm] = useState('');

  const visibleSet = new Set(visibleDeviceIndicators);

  const handleToggle = (indicatorKey: string) => {
    const userExtraSettings: UserExtraSettings = {
      ...userSettingsData?.extraSettings,
      actualitySettings: {
        ...userSettingsData?.extraSettings?.actualitySettings,
        visibleDeviceIndicators: visibleSet.has(indicatorKey)
          ? _.filter(visibleDeviceIndicators, s => s !== indicatorKey)
          : [...visibleDeviceIndicators, indicatorKey],
      },
    };

    callUpdate({
      extraSettings: userExtraSettings,
    });
  };

  const handleSelectAll = () => {
    const allIndicatorKeys = DEVICE_INDICATORS_CONFIG.map(
      indicator => indicator.key,
    );
    const userExtraSettings: UserExtraSettings = {
      ...userSettingsData?.extraSettings,
      actualitySettings: {
        ...userSettingsData?.extraSettings?.actualitySettings,
        visibleDeviceIndicators: allIndicatorKeys,
      },
    };

    callUpdate({
      extraSettings: userExtraSettings,
    });
  };

  const handleDeselectAll = () => {
    const userExtraSettings: UserExtraSettings = {
      ...userSettingsData?.extraSettings,
      actualitySettings: {
        ...userSettingsData?.extraSettings?.actualitySettings,
        visibleDeviceIndicators: [],
      },
    };

    callUpdate({
      extraSettings: userExtraSettings,
    });
  };

  // Filter indicators by search term
  const filteredIndicators = useMemo(() => {
    return searchTerm
      ? DEVICE_INDICATORS_CONFIG.filter(item =>
          item.title(t).toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : DEVICE_INDICATORS_CONFIG;
  }, [DEVICE_INDICATORS_CONFIG, searchTerm, t]);

  //   const getIndicatorIcon = (key: string) => {
  //     switch (key) {
  //       case 'agentEnergy':
  //         return Icon.Settings;
  //       case 'agentSignalStrength':
  //         return Icon.Settings;
  //       default:
  //         return Icon.Settings;
  //     }
  //   };

  const getIndicatorDescription = (key: string) => {
    switch (key) {
      case 'agentEnergy':
        return t('Shows device battery level and charging status');
      case 'agentSignalStrength':
        return t('Shows wireless signal strength and connectivity quality');
      default:
        return '';
    }
  };

  return (
    <>
      <ScreenTitle
        title={t`Device Status Indicators`}
        subtitle={t`Feed`}
        // helpNavTo={'actuality/actuality-device-indicators'}
        icon={Icon.Monitor}
        isSticky
        className="top-20"
      />

      <div className="mb-6">
        <ActionBar className="p-2">
          <InputSearch
            placeholder={t('Search device indicators...')}
            value={searchTerm}
            onChange={(value: string) => setSearchTerm(value)}
            panelTitleMode
            classNamePlaceholder="!left-2 !top-2"
            classNameInput="!px-2 !py-1"
          />
          {/* <Button
            label={t('Select All')}
            onPress={handleSelectAll}
            isDisabled={updateStatus !== AsyncLoadStatus.Ok}
            buttonType="secondary"
            buttonSize="xs"
          />
          <Button
            label={t('Deselect All')}
            onPress={handleDeselectAll}
            isDisabled={updateStatus !== AsyncLoadStatus.Ok}
            buttonType="secondary"
            buttonSize="xs"
          /> */}
        </ActionBar>

        <TitleSection
          title={t('Device Monitoring')}
          inPanelView
          classNameInner="p-4"
        >
          <ul className="space-y-6">
            {filteredIndicators.map(({ key, title, getUnitOfMeasure }) => {
              const isVisible = visibleSet.has(key);
              //   const IconComponent = getIndicatorIcon(key);

              return (
                <li
                  className={classNames('flex items-start space-x-3')}
                  key={key}
                >
                  <div className="mt-1 flex-shrink-0">
                    {/* <IconComponent className="text-menu/60 h-5 w-5" /> */}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label
                          className={classNames(
                            'block font-medium',
                            isVisible ? 'opacity-90' : 'opacity-50',
                          )}
                        >
                          {title(t)}
                        </label>
                        <p className="text-menu-text/60 mt-1 text-sm">
                          {getIndicatorDescription(key)}
                        </p>
                      </div>

                      <div className="ml-4">
                        <ButtonSwitchMulti
                          autoSize
                          options={[{ label: t('off') }, { label: t('on') }]}
                          selectedIndex={isVisible ? 1 : 0}
                          buttonType={isVisible ? 'secondary' : 'minimal'}
                          disabled={updateStatus !== AsyncLoadStatus.Ok}
                          onClick={() => handleToggle(key)}
                        />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </TitleSection>
      </div>
    </>
  );
};

export default DeviceStatusIndicatorsManagement;
